import React, { useCallback } from 'react';
import { FaeKey } from '../../types';
import { ALL_BLESSINGS_MAP } from '../../gameData';
import { highlightKeywords } from '../helpers/highlightKeywords';
import { getIconPath } from '../../utils/iconPath';
import BuildBlessingItem from "./items/BuildBlessingItem";
import OtherModifierItem from "./items/OtherModifierItem";
import { countOccurrences } from '../../utils/buildHelpers';
import { useSwornBuilderContext } from '../../contexts/SwornBuilderContext';

const BuildDetails: React.FC = () => {
    const {
        currentBuildName,
        viewedBlessing,
        selectedBlessings,
        selectedMorganaModifiers,
        selectedAnvilUpgrades,
        selectedMerlinUpgrades,
        contextMenu,
        closeContextMenu,
        openContextMenu,
        handleBlessingListItemClick,
        setViewedBlessing
    } = useSwornBuilderContext();

  const viewedBlessingData = viewedBlessing
    ? ALL_BLESSINGS_MAP.get(viewedBlessing)
    : undefined;
  const blessingDescription = viewedBlessingData && 'description' in viewedBlessingData
    ? viewedBlessingData.description
    : undefined;
  const blessingEffect = viewedBlessingData && 'effect' in viewedBlessingData
    ? viewedBlessingData.effect
    : undefined;
  const blessingType = viewedBlessingData?.type;
  const blessingFae = viewedBlessingData?.fae;

  const anvilCounts = countOccurrences(selectedAnvilUpgrades);
  const morganaCounts = countOccurrences(selectedMorganaModifiers);
  const merlinCounts = countOccurrences(selectedMerlinUpgrades);

  // --- Handlers extracted to avoid inline logic ---
  const handleBuildBannerClick = useCallback((e: React.MouseEvent) => {
    if (contextMenu) closeContextMenu();
    else openContextMenu(e, 'build', 'build', ['Rename', 'Save As', 'Save', 'Load', 'New']);
  }, [contextMenu, closeContextMenu, openContextMenu]);

  const handleBuildBannerContext = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    openContextMenu(e, 'build', 'build', ['Rename', 'Save As', 'Save', 'Load', 'New']);
  }, [openContextMenu]);

  const handleBlessingBannerClick = useCallback(() => {
    if (contextMenu) closeContextMenu();
    else setViewedBlessing(null);
  }, [contextMenu, closeContextMenu, setViewedBlessing]);

  const handleBlessingContext = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (viewedBlessing) openContextMenu(e, viewedBlessing, 'blessing');
  }, [openContextMenu, viewedBlessing]);

  const handleIconClick = useCallback(() => {
    if (contextMenu) closeContextMenu();
  }, [contextMenu, closeContextMenu]);

  const handleListClick = useCallback(() => {
    if (contextMenu) closeContextMenu();
  }, [contextMenu, closeContextMenu]);

  return (
    <div id="build-details-column">
      {/* Build Name Banner */}
      <div
        id="build-detail-banner"
        className={`interactive-element ${contextMenu ? 'no-hover' : ''}`}
        onClick={handleBuildBannerClick}
        onContextMenu={handleBuildBannerContext}
      >
        <div className="text-sworn-gold text-lg font-semibold text-center">
          {currentBuildName || 'Unnamed Build'}
        </div>
      </div>

      {/* Viewed Blessing Details */}
      {viewedBlessing && viewedBlessingData && (
        <div id="blessing-detail-view">
          <div
            id="blessing-name-banner"
            className={`interactive-element ${contextMenu ? 'no-hover' : ''}`}
            onClick={handleBlessingBannerClick}
            onContextMenu={handleBlessingContext}
          >
            <div className="text-sworn-gold text-lg font-semibold text-center">
              {viewedBlessingData.name}
            </div>
          </div>

          <div
            id="selected-blessing-icon"
            className={`interactive-element ${contextMenu ? 'no-hover' : ''}`}
            onClick={handleIconClick}
            onContextMenu={handleBlessingContext}
          >
            <img
              src={getIconPath('blessing', viewedBlessingData.name, { faeLord: blessingFae, blessingType: blessingType })}
              alt={viewedBlessingData.name}
              className="w-full h-full object-contain"
            />
          </div>

          <div id="blessing-description-details">
            <div className="text-white text-sm h-full overflow-y-auto p-2 space-y-2">
              {blessingDescription && blessingFae && (
                <p>
                  {highlightKeywords(
                    blessingDescription,
                    blessingFae as FaeKey
                  )}
                </p>
              )}
              {blessingEffect &&
               blessingFae &&
               blessingEffect !== blessingDescription &&
               !['Light Attack', 'Heavy Attack', 'Spell', 'Dash', 'Companion', 'Ultimate'].includes(blessingType || '') && (
                <p className="text-xs text-slate-300">
                  {highlightKeywords(
                    blessingEffect,
                    blessingFae as FaeKey
                  )}
                </p>
              )}
              {!blessingDescription && !blessingEffect && (
                <p>No description available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Blessing List */}
      <div
        id="build-blessing-list"
        className={`interactive-element ${contextMenu ? 'no-hover' : ''} flex flex-col`}
        onClick={handleListClick}
      >
        <div className="text-sworn-gold text-sm font-semibold text-center mb-1 shrink-0">
          Blessings ({selectedBlessings.size + Object.keys(anvilCounts).length + Object.keys(morganaCounts).length + Object.keys(merlinCounts).length})
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-1 gap-y-0.5 overflow-y-auto flex-1 content-start pr-1">
          {Array.from(selectedBlessings).map(blessingName => (
            <BuildBlessingItem
              key={blessingName}
              blessingName={blessingName}
              viewedBlessing={viewedBlessing}
              contextMenu={contextMenu}
              closeContextMenu={closeContextMenu}
              onBlessingListItemClick={handleBlessingListItemClick}
              handleContextMenu={openContextMenu}
              getIconPath={getIconPath}
            />
          ))}

          {/* Anvil Upgrades */}
          {Object.entries(anvilCounts).map(([name, count]) => (
            <OtherModifierItem key={`anvil-${name}`} name={name} type="Anvil" count={count} />
          ))}

          {/* Morgana Modifiers */}
          {Object.entries(morganaCounts).map(([name, count]) => (
            <OtherModifierItem key={`morgana-${name}`} name={name} type="Morgana" count={count} />
          ))}

          {/* Merlin Upgrades */}
          {Object.entries(merlinCounts).map(([name, count]) => (
            <OtherModifierItem key={`merlin-${name}`} name={name} type="Merlin" count={count} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BuildDetails;
